/**
 * connectorErrors.ts — reading the bring-your-own routes' answers safely.
 *
 *   readProblem      an axios error → { status, code, authorizeUrl, retryAfterSeconds }
 *                    from the RFC 9457 body (DX-19) and the Retry-After header
 *   safeReturnTo     the server's returnTo, accepted only as a same-origin path
 *   isSafeAuthorizeUrl  the service's consent link, accepted only as https
 *
 * No wording here: copy.ts turns codes into sentences.
 */
import type { ConnectorProblemBody } from '../../services/api';

export interface ConnectorProblem {
  status: number | null;
  code: string | null;
  authorizeUrl: string | null;
  retryAfterSeconds: number | null;
}

/** Retry-After as delta-seconds or an HTTP date (RFC 9110 §10.2.3). */
export function parseRetryAfter(value: unknown, now: number = Date.now()): number | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return Math.max(1, parseInt(raw, 10));
  const at = Date.parse(raw);
  if (Number.isNaN(at)) return null;
  return Math.max(1, Math.ceil((at - now) / 1000));
}

function headerOf(headers: any, name: string): unknown {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const v = headers.get(name);
    if (v !== undefined && v !== null) return v;
  }
  return headers[name] ?? headers[name.toLowerCase()];
}

export function readProblem(err: any): ConnectorProblem {
  const res = err && err.response;
  const body: ConnectorProblemBody = res && res.data && typeof res.data === 'object' ? res.data : {};
  const status = typeof (res && res.status) === 'number' ? res.status : null;
  return {
    status,
    code: typeof body.code === 'string' ? body.code : null,
    authorizeUrl: typeof body.authorizeUrl === 'string' ? body.authorizeUrl : null,
    retryAfterSeconds: parseRetryAfter(headerOf(res && res.headers, 'retry-after')),
  };
}

/**
 * Mirrors server safeReturnTo (services/connectorOAuth.js SF-2): a path that
 * starts with exactly one "/", no backslash, no control characters, and stays
 * on this origin. Anything else → "/".
 */
export function safeReturnTo(raw: unknown): string {
  if (typeof raw !== 'string' || !raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return '/';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return '/';
  try {
    const u = new URL(raw, 'https://app.invalid');
    if (u.origin !== 'https://app.invalid') return '/';
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return '/';
  }
}

/** The page only ever leaves for an https consent page (http only to a local test server). */
export function isSafeAuthorizeUrl(raw: unknown): raw is string {
  if (typeof raw !== 'string' || !raw) return false;
  try {
    const u = new URL(raw);
    if (u.protocol === 'https:') return true;
    return u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}
